import { useMemo } from "react";
import { FilterConflict, getTopMostSelected } from "../utils";
import { useTaxonomyContext } from "../../../contexts/Taxonomy/TaxonomyContext";
import useMatchProductsById from "../View/MarketLandscape/hooks/useMatchProductsById";

const REGEX = {
  PRODUCT_IDS: /\b\d+\b/g,
  COUNTRY_CODES: /\b[A-Z]{2}\b/g,
  REGION_ID_FORMAT: /\b[a-zA-Z][a-zA-Z0-9_]*\b/g,
  INDUSTRY_IDS: /(\d+(?:,\s*\d+)*)/i,
  PRODUCTS_PREFIX_PATTERN: /(.*[Pp]roducts:)\s*([\d,\s]+)/i,
  COUNTRIES_PREFIX_PATTERN: /(.*Countries:)\s*(.*)/i,
};

interface ExtractedIds {
  productIds: number[];
  countryCodes: string[];
  regionIds: string[];
  industryIds: number[];
  sicIds: number[];
  naicsIds: number[];
}

const extractProductIds = (text: string) => {
  const matches = text.match(REGEX.PRODUCT_IDS);
  return matches ? matches.map(Number) : [];
};

const extractCountryCodes = (text: string) => {
  const matches = text.match(REGEX.COUNTRY_CODES);
  return matches || [];
};

const extractRegionIds = (text: string) => {
  const matches = text.match(REGEX.REGION_ID_FORMAT);
  return matches || [];
};

const extractIndustryIds = (text: string) => {
  const matches = text.match(REGEX.INDUSTRY_IDS);
  return matches ? matches.map(Number) : [];
};

export const useEntityNameResolver = (conflicts: FilterConflict[]) => {
  const {
    sic: sicData,
    naics: naicsData,
    countryDataMap,
    regionDataMap,
    hgIndustryNameMap,
    sicNameMap,
    naicsNameMap,
    countriesLoading,
    regionsLoading,
    hgIndustriesLoading,
    sicLoading,
    naicsLoading,
  } = useTaxonomyContext();

  const sicItemsById = useMemo(() => {
    return sicData
      ? new Map(sicData.map((item) => [item.id, item]))
      : new Map();
  }, [sicData]);

  const naicsItemsById = useMemo(() => {
    return naicsData
      ? new Map(naicsData.map((item) => [item.id, item]))
      : new Map();
  }, [naicsData]);

  const extractAllIdsFromConflicts = (
    conflicts: FilterConflict[]
  ): ExtractedIds => {
    const productIds = new Set<number>();
    const countryCodes = new Set<string>();
    const regionIds = new Set<string>();
    const industryIds = new Set<number>();
    const sicIds = new Set<number>();
    const naicsIds = new Set<number>();

    for (const conflict of conflicts) {
      const { marketDefinition, filterValue, type } = conflict;
      const texts = [marketDefinition, filterValue];

      if (type === "products") {
        texts.forEach((text) =>
          extractProductIds(text).forEach((id) => productIds.add(id))
        );
      } else if (type === "geography") {
        texts.forEach((text) => {
          extractCountryCodes(text).forEach((code) => countryCodes.add(code));
          extractRegionIds(text).forEach((id) => regionIds.add(id));
        });
      } else if (type === "industry") {
        texts.forEach((text) =>
          extractIndustryIds(text).forEach((id) => industryIds.add(id))
        );
      }
    }

    return {
      productIds: [...productIds],
      countryCodes: [...countryCodes],
      regionIds: [...regionIds],
      industryIds: [...industryIds],
      sicIds: [...sicIds],
      naicsIds: [...naicsIds],
    };
  };

  // Extract IDs from conflict messages
  const { productIds, countryCodes, regionIds, industryIds, sicIds, naicsIds } =
    useMemo(() => extractAllIdsFromConflicts(conflicts), [conflicts]);

  const { loading: loadingProducts, matchedProductsById } =
    useMatchProductsById(productIds);

  const nameMap = useMemo(() => {
    const map: Record<string, string> = {};

    if (matchedProductsById?.length) {
      matchedProductsById.forEach((product) => {
        map[product.id] = product.name;
      });
    }

    countryCodes.forEach((code) => {
      const country = countryDataMap.get(code);
      if (country && country.name) {
        map[code] = `${country.name} (${code})`;
      }
    });

    regionIds.forEach((id) => {
      const region = regionDataMap.get(id);
      if (region && region.name) {
        map[id] = region.name;
      }
    });

    industryIds.forEach((id) => {
      const industryName = hgIndustryNameMap.get(id);
      if (industryName) {
        map[id] = industryName;
      }
    });
    sicIds.forEach((id) => {
      const sicName = sicNameMap.get(id.toString());
      if (sicName) {
        map[id] = sicName;
      }
    });

    naicsIds.forEach((id) => {
      const naicsName = naicsNameMap.get(id.toString());

      if (naicsName) {
        map[id] = naicsName;
      }
    });

    return map;
  }, [
    matchedProductsById,
    countryDataMap,
    regionDataMap,
    hgIndustryNameMap,
    countryCodes,
    regionIds,
    industryIds,
    sicIds,
    sicNameMap,
    naicsIds,
    naicsNameMap,
  ]);

  // Resolve conflicts with name replacements
  const resolvedConflicts = useMemo(() => {
    const isLoading =
      loadingProducts ||
      countriesLoading ||
      regionsLoading ||
      hgIndustriesLoading;

    if (isLoading) {
      return conflicts;
    }

    return conflicts.map((conflict) => {
      let { marketDefinition } = conflict;
      let { filterValue } = conflict;

      const resolveIndustryNames = (text: string) => {
        const sections = text.split("\n");

        return sections
          .map((section) => {
            const [headerType, valuesStr] = section.split("- ");
            if (!valuesStr || valuesStr === "none") return `${headerType} none`;

            const values = valuesStr.split(",").map((v) => v.trim());
            let resolvedNames: string[] = [];

            if (headerType.includes("SIC") && sicData) {
              const topMostSelected = getTopMostSelected(values, sicItemsById);
              // Resolve names for top-most items only
              resolvedNames = topMostSelected.map((id) => {
                const item = sicItemsById.get(id);
                return item ? `${item.code}: ${item.name}` : id;
              });
            } else if (headerType.includes("NAICS") && naicsData) {
              const topMostSelected = getTopMostSelected(
                values,
                naicsItemsById
              );
              resolvedNames = topMostSelected.map((id) => {
                const item = naicsItemsById.get(id);
                return item ? `${item.code}: ${item.name}` : id;
              });
            } else if (headerType.includes("Industries")) {
              resolvedNames = values.map((id) => {
                const industryName = hgIndustryNameMap.get(Number(id));
                return industryName || id;
              });
            }

            return `${headerType} ${resolvedNames.join(", ")}`;
          })
          .filter(Boolean)
          .join("\n");
      };

      if (conflict.type === "products") {
        const extractAndReplace = (text: string) => {
          // Split by newlines to handle both included and excluded sections
          const sections = text.split("\n");

          return sections
            .map((section) => {
              const productsMatch = section.match(
                REGEX.PRODUCTS_PREFIX_PATTERN
              );

              if (productsMatch) {
                const [, prefix] = productsMatch;
                const idList = productsMatch[2].split(",").map((s) => s.trim());
                const resolvedList = idList
                  .map((id) => nameMap[id] || id)
                  .join(", ");
                return `${prefix} ${resolvedList}`;
              }

              return section.replace(REGEX.PRODUCT_IDS, (match, id) => {
                return nameMap[id] || match;
              });
            })
            .join("\n");
        };

        marketDefinition = extractAndReplace(marketDefinition);
        filterValue = extractAndReplace(filterValue);
      }

      if (conflict.type === "geography") {
        const extractAndReplaceGeography = (text: string) => {
          // Split by newlines to handle both included and excluded sections
          const sections = text.split("\n");

          return sections
            .map((section) => {
              const countriesMatch = section.match(
                REGEX.COUNTRIES_PREFIX_PATTERN
              );

              if (countriesMatch) {
                const [, prefix] = countriesMatch;
                const codesList = countriesMatch[2]
                  .split(",")
                  .map((s) => s.trim());
                const countryNames = codesList
                  .map((code) => {
                    const country = countryDataMap.get(code);
                    return country?.name || code;
                  })
                  .join(", ");

                return `${prefix} ${countryNames}`;
              }

              let processedText = section;
              countryCodes.forEach((code) => {
                const country = countryDataMap.get(code);
                if (country && country.name) {
                  const regex = new RegExp(`\\b${code}\\b`, "g");
                  processedText = processedText.replace(regex, country.name);
                }
              });

              regionIds.forEach((id) => {
                const region = regionDataMap.get(id);
                if (region && region.name) {
                  const regex = new RegExp(`\\b${id}\\b`, "g");
                  processedText = processedText.replace(regex, region.name);
                }
              });

              return processedText;
            })
            .join("\n");
        };

        marketDefinition = extractAndReplaceGeography(marketDefinition);
        filterValue = extractAndReplaceGeography(filterValue);
      }
      if (conflict.type === "industry") {
        return {
          ...conflict,
          marketDefinition: resolveIndustryNames(conflict.marketDefinition),
          filterValue: resolveIndustryNames(conflict.filterValue),
        };
      }

      return {
        ...conflict,
        marketDefinition,
        filterValue,
      };
    });
  }, [
    conflicts,
    nameMap,
    loadingProducts,
    countriesLoading,
    regionsLoading,
    hgIndustriesLoading,
    countryCodes,
    regionIds,
    countryDataMap,
    regionDataMap,
    hgIndustryNameMap,
    sicItemsById,
    naicsItemsById,
    naicsData,
    sicData,
  ]);

  return {
    resolvedConflicts,
    loading:
      loadingProducts ||
      countriesLoading ||
      regionsLoading ||
      hgIndustriesLoading ||
      sicLoading ||
      naicsLoading,
  };
};
